// StepScreeningQuestions.jsx
// Dynamic screening questions driven by matched symptoms (PHQ-9, GAD-7, …)
// Computes score + severity for each assessment and passes scoreData downstream
import { useEffect, useMemo, useState } from "react";
import StepLayout from "./StepLayout";
import NavFooter  from "./NavFooter";
import PatientCard from "./PatientCard";
import styles from "./StepQuestionnaire.module.css";

import {
  getMatchingAssessments,
  calculateAssessmentScore,
  calculateAllAssessmentScores,
  getHighestSeverityResult,
  getScreeningTriage,
  SCREENING_QUESTIONNAIRES,
} from "./SelectSymptomsConstants";

// ─────────────────────────────────────────────────────────
// Score bar — visual per-assessment progress
// ─────────────────────────────────────────────────────────
const ScoreBar = ({ score, max, color }) => {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
        <span style={{ color: "#6B7280" }}>Score so far</span>
        <span style={{ fontWeight: 700, color }}>{score} / {max}</span>
      </div>
      <div style={{ height: 5, background: "#E5E7EB", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Severity chip
// ─────────────────────────────────────────────────────────
const SeverityChip = ({ label, color }) => (
  <span style={{
    display: "inline-block", fontSize: 11, fontWeight: 700,
    padding: "2px 10px", borderRadius: 99,
    background: color + "20", color, border: `1px solid ${color}40`,
  }}>
    {label}
  </span>
);

// ─────────────────────────────────────────────────────────
// Live score for a given assessment key + answers
// ─────────────────────────────────────────────────────────
const computeLiveScore = (questionnaire, answersForKey) => {
  if (!questionnaire || !answersForKey) return { score: 0, severity: null };
  let score = 0;
  questionnaire.questions.forEach((q) => {
    const ans = answersForKey[q.id];
    if (ans && typeof ans.value === "number") score += ans.value;
  });
  const severity = questionnaire.scoring?.severity?.find(
    (s) => score >= s.min && score <= s.max
  );
  return { score, severity };
};

// ─────────────────────────────────────────────────────────
// No assessment fallback
// ─────────────────────────────────────────────────────────
const NoAssessmentsCard = ({ onNext, onBack }) => (
  <StepLayout
    title="Clinical Screening"
    subtitle="Based on your reported symptoms, no specific screening questionnaire was matched."
    icon="🩺"
    footer={<NavFooter onBack={onBack} onNext={onNext} nextLabel="Continue to Analysis" />}
  >
    <div style={{ padding: "32px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
      <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.6 }}>
        Your symptom profile did not trigger a specific validated questionnaire at this time.
        We'll still perform a full triage analysis based on your symptom data.
      </p>
    </div>
  </StepLayout>
);

// ─────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────
export default function StepScreeningQuestions({
  patient,
  symptoms = [],
  initial,
  onNext,
  onBack,
}) {
  // ── Match assessments from symptoms ──────────────────────
  const assessments = useMemo(
    () => getMatchingAssessments(symptoms),
    [symptoms]
  );

  console.log("Matched assessments:", assessments, symptoms);

  // ── State ────────────────────────────────────────────────
  const [assessmentIndex, setAssessmentIndex] = useState(0);
  const [questionIndex,   setQuestionIndex]   = useState(0);
  const [answers,         setAnswers]         = useState(initial?.answers || {});
  const [showError,       setShowError]       = useState(false);

  // Auto-skip if no assessments matched
  useEffect(() => {
    if (!assessments.length) {
      onNext?.({ skipped: true, assessments: [], answers: {}, results: [], scoreData: null });
    }
  }, [assessments, onNext]);

  if (!assessments.length) return null;

  // ── Current pointers ──────────────────────────────────────
  const currentAssessment = assessments[assessmentIndex];
  const questionnaire     = currentAssessment?.config;
  const questions         = questionnaire?.questions || [];
  const currentQuestion   = questions[questionIndex];

  if (!currentAssessment || !questionnaire || !currentQuestion) return null;

  // ── Current answer for this question ─────────────────────
  const currentAnswer = answers?.[currentAssessment.key]?.[currentQuestion.id];

  // ── Live score for sidebar ────────────────────────────────
  const { score: liveScore, severity: liveSeverity } = computeLiveScore(
    questionnaire,
    answers[currentAssessment.key]
  );

  // ── Set answer ────────────────────────────────────────────
  const setAnswer = (option) => {
    setAnswers((prev) => ({
      ...prev,
      [currentAssessment.key]: {
        ...(prev[currentAssessment.key] || {}),
        [currentQuestion.id]: option,
      },
    }));
    setShowError(false);
  };

  // ── Progress across ALL assessments ──────────────────────
  const totalQuestions = assessments.reduce((acc, a) => acc + (a.config.questions?.length || 0), 0);
  const completedBefore = assessments.slice(0, assessmentIndex).reduce((acc, a) => acc + (a.config.questions?.length || 0), 0);
  const currentPosition = completedBefore + questionIndex + 1;
  const progress = Math.round((currentPosition / totalQuestions) * 100);

  const isLastQuestion   = questionIndex   === questions.length - 1;
  const isLastAssessment = assessmentIndex === assessments.length - 1;

  // ── Next ─────────────────────────────────────────────────
  const handleNext = () => {
    if (!currentAnswer) { setShowError(true); return; }

    // More questions in this assessment
    if (!isLastQuestion) {
      setQuestionIndex((p) => p + 1);
      return;
    }

    // More assessments to go
    if (!isLastAssessment) {
      setAssessmentIndex((p) => p + 1);
      setQuestionIndex(0);
      return;
    }

    // ── ALL DONE — compute final scores ──────────────────────
    const finalAnswers = {
      ...answers,
      [currentAssessment.key]: {
        ...(answers[currentAssessment.key] || {}),
        [currentQuestion.id]: currentAnswer,
      },
    };

    const results = assessments.map((a) =>
      calculateAssessmentScore(a.config, finalAnswers[a.key] || {}, a.key)
    );

    const highest     = getHighestSeverityResult(results);
    const triageMeta  = getScreeningTriage(results);

    // Aggregate scoreData consumed by StepTriage + StepResults
    const scoreData = {
      results,             // per-assessment full result objects
      highest,             // the worst result object
      triageMeta,          // { code, label, color, urgent? }
      totalScore: results.reduce((s, r) => s + (r?.totalScore || 0), 0),
      maxScore:   results.reduce((s, r) => s + (r?.maxScore   || 0), 0),
    };

    onNext?.({
      skipped: false,
      assessments,
      answers: finalAnswers,
      results,
      scoreData,
    });
  };

  // ── Back ─────────────────────────────────────────────────
  const handleBack = () => {
    if (questionIndex > 0) { setQuestionIndex((p) => p - 1); return; }
    if (assessmentIndex > 0) {
      const prev = assessments[assessmentIndex - 1];
      setAssessmentIndex((p) => p - 1);
      setQuestionIndex((prev?.config?.questions?.length || 1) - 1);
      return;
    }
    onBack?.();
  };

  // ── Severity colour helper ────────────────────────────────
  const severityColor = liveSeverity?.color || "#6B7280";

    const storedHistory = JSON.parse(
    localStorage.getItem("medicalHistory") || "{}"
  );

  const mapPreScreenToHistory = (data) => {
    if (!data) return {};
    return {
      condition:
        data.hasCondition === "Yes"
          ? data.diagnosed || true
          : false,
      takingMeds: data.takingMeds === "Yes",
      allergy: data.hasAllergy,
      surgery:
        data.hasSurgery === "Yes"
          ? data.surgeryWhen || true
          : false,
    };
  };

  const historyData = mapPreScreenToHistory(storedHistory);

  // ─────────────────────────────────────────────────────────
  return (
    <StepLayout
      title={questionnaire.title}
      subtitle={questionnaire.instructions}
      icon={questionnaire.icon}
      footer={
        <NavFooter
          onBack={handleBack}
          onNext={handleNext}
          nextLabel={
            isLastAssessment && isLastQuestion ? "Finish & Analyse" : "Continue"
          }
        />
      }
    >
      <div className={styles.wrapper}>

        {/* ── LEFT — Question ── */}
        <div className={styles.left}>

          {/* Overall progress bar */}
          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>

          {/* Assessment label */}
          <div className={styles.group}>
            <div className={styles.groupTitle}>
              <span>{questionnaire.icon}</span>
              {questionnaire.shortTitle}
              {/* {assessments.length > 1 && (
                <span style={{ marginLeft: 8, fontSize: 11, color: "#9CA3AF", fontWeight: 500 }}>
                  ({assessmentIndex + 1} of {assessments.length})
                </span>
              )} */}
            </div>

            {/* Question card */}
            <div className={styles.cards}>
              <div className={`${styles.card} ${showError ? styles.cardError : ""}`}>

                {/* Question header */}
                <div className={styles.cardTopRow}>
                  <p className={styles.cardQuestion}>
                    {currentQuestion.text}
                    {currentQuestion.critical && (
                      <span style={{
                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                        color: "#DC2626", background: "#FEE2E2",
                        padding: "1px 6px", borderRadius: 4,
                      }}>
                        ⚠️ Critical
                      </span>
                    )}
                  </p>
                  <span className={styles.cardIndex}>
                    Q{currentPosition} / {totalQuestions}
                  </span>
                </div>

                {/* Options */}
                <div className={styles.options}>
                  {currentQuestion.options?.map((option) => {
                    const selected = currentAnswer?.id === option.id;
                    return (
                      <button
                        key={option.id}
                        className={`${styles.option} ${selected ? styles.optionSelected : ""}`}
                        onClick={() => setAnswer(option)}
                      >
                        <div>{option.label}</div>
                        {/* <small style={{ color: selected ? "inherit" : "#9CA3AF" }}>
                          Score: {option.value}
                        </small> */}
                      </button>
                    );
                  })}
                </div>

                {/* Critical question warning */}
                {currentQuestion.critical && currentAnswer?.value > 0 && (
                  <div style={{
                    marginTop: 12, padding: "10px 14px",
                    background: "#FEF2F2", border: "1.5px solid #FECACA",
                    borderRadius: 8, fontSize: 13, color: "#991B1B", fontWeight: 600,
                  }}>
                    ⚠️ This response requires immediate clinical attention. A care team member will be alerted.
                  </div>
                )}

                {showError && (
                  <p className={styles.errorText}>Please select an option to continue.</p>
                )}
              </div>
            </div>

            {/* Live score bar */}
            {/* <div style={{ marginTop: 12, padding: "12px 16px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10 }}>
              <ScoreBar score={liveScore} max={questionnaire.scoring?.max || 0} color={severityColor} />
              {liveSeverity && (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#6B7280" }}>Current severity:</span>
                  <SeverityChip label={liveSeverity.label} color={severityColor} />
                </div>
              )}
            </div> */}
          </div>
        </div>

        {/* ── RIGHT — Sidebar ── */}
        <div className={styles.right}>
          <PatientCard patient={patient} size={130} />
          <div className={styles.historyCard}>
            <h4 className={styles.historyTitle}>
              Medical History
            </h4>

            <ul className={styles.historyList}>
              {Object.entries(historyData).length > 0 ? (
                Object.entries(historyData).map(
                  ([key, value], i) => {
                    let label = "";
                    let icon = "?";

                    switch (key) {
                      case "condition":
                        label =
                          value && value !== true
                            ? `Diagnosed with ${value}`
                            : "Has medical condition";
                        icon = value ? "✔" : "✖";
                        break;

                      case "takingMeds":
                        label =
                          "Currently taking medications";
                        icon = value ? "✔" : "✖";
                        break;

                      case "allergy":
                        label = "Has allergies";
                        icon =
                          value === "Yes"
                            ? "✔"
                            : value === "No"
                            ? "✖"
                            : "?";
                        break;

                      case "surgery":
                        label =
                          value && value !== true
                            ? `Surgery: ${value}`
                            : "Past surgery";
                        icon = value ? "✔" : "✖";
                        break;
                    }

                    return (
                      <li key={i} className={styles.historyItem}>
                        <span className={styles.historyDot}>
                          {icon}
                        </span>
                        {label}
                      </li>
                    );
                  }
                )
              ) : (
                <li className={styles.historyEmpty}>
                  No history available
                </li>
              )}
            </ul>
          </div>

          {/* <div className={styles.historyCard}>
            <h4 className={styles.historyTitle}>Screening Summary</h4>
            <ul className={styles.historyList}>
              <li className={styles.historyItem}>
                <span className={styles.historyDot}>{questionnaire.icon}</span>
                {questionnaire.shortTitle}
              </li>
              <li className={styles.historyItem}>
                <span className={styles.historyDot}>❓</span>
                Question {questionIndex + 1} / {questions.length}
              </li>
              <li className={styles.historyItem}>
                <span className={styles.historyDot}>📊</span>
                Progress {progress}%
              </li>
              <li className={styles.historyItem}>
                <span className={styles.historyDot}>🎯</span>
                Score: <strong style={{ color: severityColor, marginLeft: 4 }}>{liveScore}</strong>
                <span style={{ color: "#9CA3AF" }}> / {questionnaire.scoring?.max}</span>
              </li>
              {liveSeverity && (
                <li className={styles.historyItem} style={{ paddingTop: 4 }}>
                  <span className={styles.historyDot}>🏷️</span>
                  <SeverityChip label={liveSeverity.label} color={severityColor} />
                </li>
              )}
            </ul>
            {assessments.length > 1 && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #E5E7EB" }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Assessments queued
                </p>
                {assessments.map((a, i) => (
                  <div key={a.key} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "5px 0", fontSize: 12,
                    color: i === assessmentIndex ? "#111827" : "#9CA3AF",
                    fontWeight: i === assessmentIndex ? 700 : 400,
                  }}>
                    <span>{i < assessmentIndex ? "✅" : i === assessmentIndex ? "▶" : "○"}</span>
                    {a.config.shortTitle}
                  </div>
                ))}
              </div>
            )}
          </div> */}
        </div>
      </div>
    </StepLayout>
  );
}
